import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Pillar } from 'chery-contracts';
import { ArrowDown, ArrowUp, GripVertical, Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react';
import { useMemo, useState, type DragEvent, type ReactNode } from 'react';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Input } from '../../components/ui/Input';
import { Notice, type NoticeTone } from '../../components/ui/Notice';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TableCell, TableHead, TableHeaderCell, TableRow } from '../../components/ui/Table';
import { cn } from '../../lib/cn';
import { serverMessage } from '../../lib/form-helpers';
import { PILLAR_CLASSES, PILLAR_META, PILLARS } from '../../lib/pillars';
import { useDebouncedValue } from '../../lib/use-debounced-value';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { DeactivateKpiDialog, DeleteKpiDialog } from './KpiGuardDialogs';
import { KpiSheet, type KpiSheetMode } from './KpiSheet';
import { canDropOn, canManageKpis, groupByPillar, indentRem, moveInList, siblingMove, unitShortLabel, type KpiRow } from './kpi-display';

interface PageNotice {
  tone: NoticeTone;
  text: string;
}

type SheetState = { mode: 'create' } | { mode: Exclude<KpiSheetMode, 'create'>; kpi: KpiRow } | null;

/**
 * KPI Setup (plan §7): the catalogue grouped by pillar with the hierarchy indented. ADMIN and PM reorder (drag or
 * ↑/↓, among siblings), edit, deactivate and delete; every other role gets the same list with no edit controls at
 * all, and a row opens a read-only details panel. What a role may see is decided by the service (`kpis.list` is
 * pillar-scoped), never filtered here.
 */
export const KpisPage = () => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage = canManageKpis(user?.role);

  const [search, setSearch] = useState('');
  const [pillar, setPillar] = useState<Pillar | ''>('');
  const [showInactive, setShowInactive] = useState(true);
  const [notice, setNotice] = useState<PageNotice | null>(null);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [deleting, setDeleting] = useState<KpiRow | null>(null);
  const [deactivating, setDeactivating] = useState<KpiRow | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim());
  const listQuery = useQuery(
    orpc.kpis.list.queryOptions({ input: { pillar: pillar || undefined, search: debouncedSearch || undefined, includeInactive: showInactive } }),
  );
  // Every KPI the caller may see, inactive included: the reorder baseline (the service validates against the whole
  // pillar) and the parent choices of the sheet.
  const allQuery = useQuery(orpc.kpis.list.queryOptions({ input: { includeInactive: true } }));

  const rows = useMemo(() => listQuery.data?.items ?? [], [listQuery.data]);
  const allRows = useMemo(() => allQuery.data?.items ?? [], [allQuery.data]);
  const groups = useMemo(() => groupByPillar(rows), [rows]);
  const canReorder = canManage && debouncedSearch === '' && search.trim() === '';

  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.kpis.key() });

  const reorder = useMutation(orpc.kpis.reorder.mutationOptions());
  const activate = useMutation(orpc.kpis.update.mutationOptions());

  const pillarIds = (each: Pillar): string[] => allRows.filter((row) => row.pillar === each).map((row) => row.id);

  const submitOrder = async (each: Pillar, orderedIds: string[]): Promise<void> => {
    try {
      await reorder.mutateAsync({ pillar: each, orderedIds });
      await invalidate();
      setNotice({ tone: 'success', text: t('kpis.reordered') });
    } catch (error) {
      await invalidate();
      setNotice({ tone: 'danger', text: serverMessage(error) ?? t('errors.saveFailed') });
    }
  };

  const move = (row: KpiRow, direction: 'up' | 'down'): void => {
    const next = siblingMove(rows, pillarIds(row.pillar), row.id, direction);

    if (next) {
      void submitOrder(row.pillar, next);
    }
  };

  const onDrop = (event: DragEvent, target: KpiRow): void => {
    event.preventDefault();

    if (dragId && canDropOn(rows, dragId, target.id)) {
      void submitOrder(target.pillar, moveInList(pillarIds(target.pillar), dragId, target.id));
    }

    setDragId(null);
    setDropId(null);
  };

  const onActivate = async (row: KpiRow): Promise<void> => {
    try {
      await activate.mutateAsync({ id: row.id, status: 'ACTIVE' });
      await invalidate();
      setNotice({ tone: 'success', text: t('kpis.activated', { name: row.name }) });
    } catch (error) {
      setNotice({ tone: 'danger', text: serverMessage(error) ?? t('errors.saveFailed') });
    }
  };

  const pillarOptions = [{ value: '', label: t('kpis.allPillars') }, ...PILLARS.map((each) => ({ value: each, label: PILLAR_META[each].label }))];
  const isBusy = reorder.isPending || activate.isPending;
  const columnCount = canManage ? 8 : 6;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('pages.setupKpis')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('kpis.subtitle')}</p>
        </div>
        {canManage ? (
          <Button onClick={() => setSheet({ mode: 'create' })}>
            <Plus className="size-4" />
            {t('kpis.new')}
          </Button>
        ) : null}
      </div>

      {canManage ? null : <Notice>{t('kpis.readOnly')}</Notice>}

      {notice ? (
        <Notice
          tone={notice.tone}
          onDismiss={() => setNotice(null)}
        >
          {notice.text}
        </Notice>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('kpis.search')}
          aria-label={t('kpis.search')}
          className="w-64"
        />
        <Select
          value={pillar}
          onChange={(event) => setPillar(event.target.value as Pillar | '')}
          options={pillarOptions}
          aria-label={t('kpis.form.pillar')}
          className="w-56"
        />
        <Checkbox
          id="kpis-show-inactive"
          label={t('kpis.showInactive')}
          checked={showInactive}
          onChange={(event) => setShowInactive(event.target.checked)}
        />
        {canManage ? <p className="ms-auto text-xs text-muted-foreground">{canReorder ? t('kpis.reorderHint') : t('kpis.reorderDisabled')}</p> : null}
      </div>

      <Table aria-busy={isBusy || listQuery.isFetching}>
        <TableHead>
          <tr>
            {canManage ? <TableHeaderCell className="w-8 px-2" /> : null}
            <TableHeaderCell>{t('kpis.columns.code')}</TableHeaderCell>
            <TableHeaderCell>{t('kpis.columns.name')}</TableHeaderCell>
            <TableHeaderCell>{t('kpis.columns.division')}</TableHeaderCell>
            <TableHeaderCell>{t('kpis.columns.unit')}</TableHeaderCell>
            <TableHeaderCell>{t('kpis.columns.valueType')}</TableHeaderCell>
            <TableHeaderCell>{t('kpis.columns.dataSource')}</TableHeaderCell>
            {canManage ? <TableHeaderCell className="text-end">{t('kpis.columns.actions')}</TableHeaderCell> : null}
          </tr>
        </TableHead>

        {listQuery.isLoading ? (
          <tbody>
            <tr>
              <td
                colSpan={columnCount}
                className="px-4 py-12 text-center text-muted-foreground"
              >
                <span className="inline-flex items-center gap-2">
                  <Spinner />
                  {t('app.loading')}
                </span>
              </td>
            </tr>
          </tbody>
        ) : listQuery.isError ? (
          <tbody>
            <tr>
              <td
                colSpan={columnCount}
                className="px-4 py-12 text-center text-destructive"
              >
                {t('errors.loadFailed')}
              </td>
            </tr>
          </tbody>
        ) : groups.length === 0 ? (
          <tbody>
            <tr>
              <td
                colSpan={columnCount}
                className="px-4 py-12 text-center text-muted-foreground"
              >
                {t('kpis.empty')}
              </td>
            </tr>
          </tbody>
        ) : (
          groups.map((group) => {
            const classes = PILLAR_CLASSES[group.pillar];

            return (
              <tbody
                key={group.pillar}
                data-pillar={group.pillar}
              >
                <tr className={cn('border-b', classes.band, classes.border)}>
                  <th
                    colSpan={columnCount}
                    scope="rowgroup"
                    className={cn('px-4 py-2 text-start text-sm font-semibold', classes.text)}
                  >
                    {PILLAR_META[group.pillar].label}
                    <span className="ms-2 text-xs font-normal opacity-80">{t('kpis.count', { count: group.rows.length })}</span>
                  </th>
                </tr>

                {group.rows.map((row) => {
                  const siblings = group.rows.filter((each) => each.parentKpiId === row.parentKpiId);
                  const position = siblings.findIndex((each) => each.id === row.id);
                  const isInactive = row.status === 'INACTIVE';

                  return (
                    <TableRow
                      key={row.id}
                      data-kpi-code={row.code}
                      draggable={canReorder && !isBusy}
                      onDragStart={(event) => {
                        setDragId(row.id);
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', row.id);
                      }}
                      onDragOver={(event) => {
                        if (dragId && canDropOn(rows, dragId, row.id)) {
                          event.preventDefault();
                          setDropId(row.id);
                        }
                      }}
                      onDragLeave={() => setDropId((current) => (current === row.id ? null : current))}
                      onDragEnd={() => {
                        setDragId(null);
                        setDropId(null);
                      }}
                      onDrop={(event) => onDrop(event, row)}
                      onClick={canManage ? undefined : () => setSheet({ mode: 'view', kpi: row })}
                      className={cn(
                        isInactive && 'text-muted-foreground',
                        !canManage && 'cursor-pointer transition-colors hover:bg-muted/60',
                        dragId === row.id && 'opacity-50',
                        dropId === row.id && 'bg-primary/5 outline outline-1 -outline-offset-1 outline-ring',
                      )}
                    >
                      {canManage ? (
                        <TableCell className="w-8 px-2">
                          {canReorder ? (
                            <GripVertical
                              aria-label={t('kpis.dragHandle')}
                              className="size-4 cursor-grab text-muted-foreground"
                            />
                          ) : null}
                        </TableCell>
                      ) : null}
                      <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">{row.code}</TableCell>
                      <TableCell>
                        <div
                          className="flex items-center gap-2"
                          style={{ paddingInlineStart: `${indentRem(row.depth)}rem` }}
                        >
                          {row.depth > 0 ? <span className="text-muted-foreground">└</span> : null}
                          <span className={cn('font-medium', isInactive ? 'text-muted-foreground' : 'text-foreground')}>{row.name}</span>
                          {row.source === 'CALCULATED' ? (
                            <span
                              title={t('enums.source.CALCULATED')}
                              className="font-serif text-xs italic text-violet-700/80 dark:text-violet-300/80"
                            >
                              ƒ
                            </span>
                          ) : null}
                          {isInactive ? <Badge tone="outline">{t('enums.status.INACTIVE')}</Badge> : null}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">{row.division ?? '—'}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs">{unitShortLabel(row.unit, row.currency)}</TableCell>
                      <TableCell
                        className="whitespace-nowrap text-xs"
                        title={t(`enums.valueTypeHint.${row.valueType}`)}
                      >
                        {t(`enums.valueType.${row.valueType}`)}
                      </TableCell>
                      <TableCell className="max-w-[12rem] truncate text-xs text-muted-foreground">{row.dataSource ?? '—'}</TableCell>
                      {canManage ? (
                        <TableCell className="whitespace-nowrap text-end">
                          <div className="inline-flex items-center gap-0.5">
                            {canReorder ? (
                              <>
                                <IconButton
                                  label={t('actions.moveUp')}
                                  disabled={isBusy || position <= 0}
                                  onClick={() => move(row, 'up')}
                                >
                                  <ArrowUp className="size-3.5" />
                                </IconButton>
                                <IconButton
                                  label={t('actions.moveDown')}
                                  disabled={isBusy || position >= siblings.length - 1}
                                  onClick={() => move(row, 'down')}
                                >
                                  <ArrowDown className="size-3.5" />
                                </IconButton>
                              </>
                            ) : null}
                            <IconButton
                              label={t('actions.edit')}
                              onClick={() => setSheet({ mode: 'edit', kpi: row })}
                            >
                              <Pencil className="size-3.5" />
                            </IconButton>
                            {isInactive ? (
                              <IconButton
                                label={t('actions.activate')}
                                disabled={isBusy}
                                onClick={() => void onActivate(row)}
                              >
                                <Power className="size-3.5" />
                              </IconButton>
                            ) : (
                              <IconButton
                                label={t('actions.deactivate')}
                                onClick={() => setDeactivating(row)}
                              >
                                <PowerOff className="size-3.5" />
                              </IconButton>
                            )}
                            <IconButton
                              label={t('actions.delete')}
                              onClick={() => setDeleting(row)}
                              className="hover:text-destructive"
                            >
                              <Trash2 className="size-3.5" />
                            </IconButton>
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </tbody>
            );
          })
        )}
      </Table>

      <KpiSheet
        isOpen={sheet !== null}
        mode={sheet?.mode ?? 'create'}
        kpi={sheet && sheet.mode !== 'create' ? sheet.kpi : undefined}
        allRows={allRows}
        onClose={() => setSheet(null)}
        onSaved={(name, created) => {
          setSheet(null);
          setNotice({ tone: 'success', text: created ? t('kpis.created', { name }) : t('kpis.saved', { name }) });
          void invalidate();
        }}
      />

      <DeleteKpiDialog
        kpi={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(name) => {
          setDeleting(null);
          setNotice({ tone: 'success', text: t('kpis.deleted', { name }) });
          void invalidate();
        }}
        onDeactivateInstead={(row) => {
          setDeleting(null);
          setDeactivating(row);
        }}
      />

      <DeactivateKpiDialog
        kpi={deactivating}
        onClose={() => setDeactivating(null)}
        onDeactivated={(name) => {
          setDeactivating(null);
          setNotice({ tone: 'success', text: t('kpis.deactivated', { name }) });
          void invalidate();
        }}
      />
    </div>
  );
};

interface IconButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

const IconButton = ({ label, onClick, disabled, className, children }: IconButtonProps) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30',
      className,
    )}
  >
    {children}
  </button>
);
